"use client";

import { PropsWithChildren, useEffect } from "react";
import { useAuth, useOrganization } from "@clerk/nextjs";
import { KeyedMutator } from "swr";

import { IOrganization } from "@/backend/domains/organization/domain/Organization";
import { LoaderFullScreen } from "@/client/shared/ui/loader-full-screen";

import config from "@/config/config";
import { execAction } from "@/lib/executeServerAction";
import { useBaseServerAction } from "@/hooks/useServerAction";
import { BaseStoreProps, createAdvancedStore } from "@/stores/createAdvancedStore";
import { useYandexMetrica } from "@/components/yandex-metrika";
import { getOrganization, updateOrganizationName } from "@/actions/organization";

//

export interface OrganizationStore extends BaseStoreProps {
  organization: IOrganization;
  mutate: KeyedMutator<IOrganization | undefined>;
}

export const organizationStore = createAdvancedStore<OrganizationStore>("Organization");

export const useNullableOrganizationStore = organizationStore.useNullableContext;

export const useOrganizationStore = () => {
  return organizationStore.useContext((s) => s.organization);
};

export const useMutateOrganization = () => {
  return organizationStore.useContext((s) => s.mutate);
};

//

export const OrganizationProvider: React.FC<PropsWithChildren<{ fallback?: React.ReactNode }>> = ({
  children,
  fallback,
}) => {
  const { orgId } = useAuth();
  const { userParams, params } = useYandexMetrica();
  const orgData = useOrganization({ memberships: true });
  const organizationResourse = orgData.organization;

  const { data: organization, mutate: mutateOrganization } = useBaseServerAction(
    { key: "organization", orgId },
    () => {
      if (!orgId) return;
      return getOrganization({ orgId });
    },
  );

  useEffect(() => {
    if (!organization) return;
    if (!organizationResourse) return;
    if (organization.name === organizationResourse.name) return;
    if (config.DEVELOPMENT_CLERK_ORG_ID) return;
    const promise = execAction(() => updateOrganizationName(organizationResourse.name));
    mutateOrganization(promise).catch((e) => console.error(e));
  }, [organization, mutateOrganization, organizationResourse]);

  useEffect(() => {
    if (!organization) return;
    userParams({ UserID: organization.id });
    params({ organization: organization });
  }, [organization, params, userParams]);

  //

  if (orgId === null) return fallback;
  if (!organization) return <LoaderFullScreen />;

  return (
    <organizationStore.Provider value={{ organization, mutate: mutateOrganization }}>
      {children}
    </organizationStore.Provider>
  );
};
